import { useQuery } from 'react-query';
import { useDebounceValue } from 'usehooks-ts';
import {
  fetchWfsCapabilities,
  type IWfsCapabilities,
} from '../../layers/wfs/parseWfsCapabilities.ts';
import { isIntrospectableLayerUrl } from '../../metadata/fetchLayerMetadata.ts';

const CAPABILITIES_STALE_TIME = 10 * 60 * 1000;
const CAPABILITIES_CACHE_TIME = 30 * 60 * 1000;

/**
 * GetCapabilities di un servizio WFS con caching per URL: alimenta la
 * select dei feature type e la rilevazione di versione/formato/CRS a
 * config-time.
 */
export function useWfsCapabilities(url: string | undefined): {
  capabilities: IWfsCapabilities | null;
  isLoading: boolean;
  isError: boolean;
} {
  const trimmedUrl = url?.trim() ?? '';
  const [debouncedUrl] = useDebounceValue(trimmedUrl, 300);

  const enabled = isIntrospectableLayerUrl(debouncedUrl);

  const query = useQuery(
    ['wfs-capabilities', debouncedUrl],
    () => fetchWfsCapabilities(debouncedUrl),
    {
      enabled,
      staleTime: CAPABILITIES_STALE_TIME,
      cacheTime: CAPABILITIES_CACHE_TIME,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    capabilities: query.data ?? null,
    isLoading: enabled && (query.isLoading || debouncedUrl !== trimmedUrl),
    isError: query.isError,
  };
}
